<template>
  <div v-if="isOpen" class="fixed inset-0 z-[9999] flex justify-end">
    <!-- Fundo escuro (Backdrop) -->
    <div class="absolute inset-0 bg-slate-900/30 backdrop-blur-sm dark:bg-black/50 transition-opacity" @click="closeSidebar"></div>
    
    <!-- Sidebar -->
    <div class="relative w-full max-w-[360px] h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col border-l border-slate-200 dark:border-slate-800 transform transition-transform">
      
      <!-- Cabeçalho -->
      <div class="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
        <div class="flex items-center gap-3">
          <h3 class="font-bold text-lg text-slate-800 dark:text-slate-100 m-0 leading-none">Fila de Alertas</h3>
          <span v-if="alerts.length > 0" class="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-sm">
            {{ alerts.length }}
          </span>
        </div>
        <button @click="closeSidebar" class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Opções de Ordenação -->
      <div v-if="alerts.length > 0" class="px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 shadow-sm z-10">
        <label class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 block">Ordenar por</label>
        <select v-model="sortBy" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm rounded-lg px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-woot-500 cursor-pointer shadow-sm transition-colors hover:border-slate-300 dark:hover:border-slate-600">
          <option value="time">Tempo de espera (Maior primeiro)</option>
          <option value="priority">Prioridade do nível</option>
        </select>
      </div>
      
      <!-- Corpo / Lista -->
      <div class="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30 dark:bg-slate-900">
        
        <!-- Estado Vazio -->
        <div v-if="alerts.length === 0" class="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
          <div class="bg-slate-100 dark:bg-slate-800 p-4 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-8 h-8">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <p class="text-sm font-medium">Nenhum alerta pendente no momento.</p>
        </div>
        
        <!-- Itens -->
        <div
          v-for="(alert, index) in sortedAlerts"
          :key="alert.conversation.id"
          @click="goToConversation(alert)"
          class="group relative flex items-start gap-4 p-4 rounded-2xl bg-white dark:bg-slate-800 cursor-pointer transition-all border border-slate-200/60 dark:border-slate-700/60 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden"
          :style="{ borderLeftColor: alert.alert_rule.color, borderLeftWidth: '4px' }"
        >
          <!-- Fundo sutil colorido no hover -->
          <div class="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity" :style="{ backgroundColor: alert.alert_rule.color }"></div>
          
          <div 
            class="relative flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm ring-4 ring-white dark:ring-slate-800"
            :style="{ backgroundColor: alert.alert_rule.color }"
          >
            {{ index + 1 }}
          </div>
          
          <div class="relative flex-1 min-w-0 pt-0.5">
            <div class="flex justify-between items-start mb-1.5">
              <h4 class="text-sm font-bold text-slate-800 dark:text-slate-100 truncate pr-2 leading-tight">
                {{ alert.conversation.contact_name || 'Cliente Desconhecido' }}
              </h4>
              <span class="text-xs font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-900 shadow-inner" :style="{ color: alert.alert_rule.color }">
                {{ formatWaitTime(alert.conversation.waiting_since) }}
              </span>
            </div>
            
            <div class="flex items-center gap-1.5">
              <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: alert.alert_rule.color }"></span>
              <span class="text-[11px] font-semibold tracking-wide text-slate-500 dark:text-slate-400 uppercase truncate">
                {{ alert.alert_rule.name }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AlertQueueSidebar',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      alerts: 'alertQueue/getAlerts',
      accountId: 'getCurrentAccountId',
    }),
    sortedAlerts() {
      if (!this.alerts) return [];
      
      return [...this.alerts].sort((a, b) => {
        if (this.sortBy === 'time') {
          // Maior tempo de espera primeiro (waiting_since MENOR = mais tempo esperando)
          return a.conversation.waiting_since - b.conversation.waiting_since;
        } else {
          // Ordenar por prioridade (1 é mais prioritário, então cresce), depois por tempo
          if (a.alert_rule.priority !== b.alert_rule.priority) {
            return a.alert_rule.priority - b.alert_rule.priority;
          }
          return a.conversation.waiting_since - b.conversation.waiting_since;
        }
      });
    },
  },
  data() {
    return {
      sortBy: 'time',
    };
  },
  mounted() {
    // Inicia o polling quando a sidebar é montada na aplicacao
    this.$store.dispatch('alertQueue/startPolling');
    
    // Atualiza o relogio interno pra tela re-renderizar os tempos
    this.timer = setInterval(() => {
      this.$forceUpdate();
    }, 1000);
  },
  beforeDestroy() {
    this.$store.dispatch('alertQueue/stopPolling');
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    closeSidebar() {
      this.$emit('close');
    },
    formatWaitTime(waitingSince) {
      if (!waitingSince) return '00:00';
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - waitingSince * 1000) / 1000);
      
      if (diffInSeconds < 0) return '00:00';
      
      const totalMinutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      
      if (totalMinutes >= 60) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }
      
      return `${String(totalMinutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    goToConversation(alert) {
      const conversation = alert.conversation;
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.accountId,
          inbox_id: conversation.inbox_id || 0,
          conversation_id: conversation.id,
        },
      });
      this.closeSidebar();
    },
  },
};
</script>

<style scoped>
/* Scoped styles replaced by Tailwind classes */
</style>
